/* Provider adapters. Every adapter's fetch() resolves to the neutral payload the renderer consumes:
   { average, total, distribution: { "1".."5": count },
     reviews: [{ rating, title, body, author, location, verified, attributeLabel, attributeValue }] }
   Adding a provider = a new entry here + a provider block in product-reviews-hero.css
   (widget hide/reveal selectors) + an option in the "Reviews provider" theme setting. */
const REVIEWS_ADAPTERS = {
	yotpo: {
		API_BASE: 'https://api.yotpo.com/v1/widget',
		// Full-widget elements on the page — used by "Read all" reveal + scroll fallbacks.
		widgetSelectors: '.yotpo-reviews-main-widget, .yotpo-widget-instance',

		async fetch({ appKey, productId, limit, signal }) {
			// One request returns both the bottomline aggregates and the latest reviews.
			// Over-fetch: Yotpo filters reviews AFTER paginating, so small per_page values can
			// come back short (observed: per_page=3 -> 2 reviews on a 6k-review product).
			const perPage = Math.max(limit * 3, 10);
			const url = `${this.API_BASE}/${appKey}/products/${productId}/reviews.json?per_page=${perPage}`;
			const response = await fetch(url, { credentials: 'omit', signal });
			if (!response.ok) {
				throw new Error(`Yotpo ${response.status}`);
			}
			const json = await response.json();
			const bottomline = json?.response?.bottomline;
			if (!bottomline) {
				throw new Error('Missing bottomline');
			}
			const reviews = Array.isArray(json?.response?.reviews) ? json.response.reviews : [];
			return {
				average: Number(bottomline.average_score) || 0,
				total: Number(bottomline.total_review) || 0,
				distribution: bottomline.star_distribution || {},
				reviews: reviews.slice(0, limit).map((review) => this.normalizeReview(review)),
			};
		},

		normalizeReview(review) {
			// Flavor/variant custom field (Flavor, Size, Scent, etc.); absent when the review has none.
			const variantField =
				this.customFields(review).find((field) => /flavou?r|variant|variation|size|scent|colou?r/i.test(field.label)) ||
				null;
			return {
				rating: Number(review.score) || 0,
				title: review.title || '',
				body: review.content || '',
				author: review.user?.display_name || '',
				// Location isn't a consistent Yotpo field — try known spots + a location-ish custom field.
				location:
					review.user?.location ||
					review.metadata?.location ||
					review.geo?.country_name ||
					this.findCustomField(review, /^(location|city|region)$/i),
				verified: Boolean(review.verified_buyer),
				attributeLabel: variantField ? variantField.label : '',
				attributeValue: variantField ? variantField.value : '',
			};
		},

		// Normalize Yotpo's varying custom-question shapes into a flat [{ label, value }] list.
		customFields(review) {
			const result = [];
			const pushPair = (label, value) => {
				if (label && value) {
					result.push({ label: String(label), value: String(value) });
				}
			};
			// Array shapes (newer widgets).
			const arrayShapes = []
				.concat(review.custom_fields_attributes || [])
				.concat(review.custom_questions_answers || [])
				.concat(review.review_attributes || []);
			for (const field of arrayShapes) {
				pushPair(
					field.title || field.label || field.name || field.question_text || field.key,
					field.value || field.answer || field.text
				);
			}
			// Object-keyed-by-id shape (legacy Yotpo).
			const fieldsObject = review.custom_fields;
			if (fieldsObject && typeof fieldsObject === 'object' && !Array.isArray(fieldsObject)) {
				for (const field of Object.values(fieldsObject)) {
					if (field && typeof field === 'object') {
						pushPair(field.title || field.name || field.label, field.value || field.answer);
					}
				}
			}
			return result;
		},

		findCustomField(review, pattern) {
			const match = this.customFields(review).find((field) => pattern.test(field.label));
			return match ? match.value : '';
		},

		findWriteReviewButton() {
			// Yotpo's button class varies by widget version — try specific→generic, then text match.
			const selectors = [
				'.yotpo-new-review-btn',
				'.yotpo-new-review-btn-wrapper button',
				'.yotpo-new-review-btn-wrapper [role="button"]',
				'.yotpo-new-review-btn-wrapper a',
				'.yotpo-default-button.write-review-button',
				'.write-review-button',
				'[class*="WriteReviewButton"]',
			];
			for (const selector of selectors) {
				const match = document.querySelector(selector);
				if (match) {
					return match;
				}
			}
			// Last resort: any button/anchor whose text says "write a review".
			const candidates = document.querySelectorAll(
				'.yotpo-widget-instance button, .yotpo-widget-instance a, .yotpo-reviews-main-widget button, .yotpo-reviews-main-widget a'
			);
			for (const candidate of candidates) {
				if (/write\s+a?\s*review/i.test(candidate.textContent || '')) {
					return candidate;
				}
			}
			return null;
		},
	},
};

class ProductReviewsHero extends HTMLElement {
	static CACHE_TTL_MS = 30 * 60 * 1000;
	static CACHE_PREFIX = 'product-reviews-hero:v5:';
	static REVIEW_LIMIT = 3;
	static EMPTY_BUTTON_LABEL = 'Be the first to write a review';
	// Floor for a stalled reviews request: abort so the skeleton falls through to the error state
	// instead of shimmering indefinitely.
	static FETCH_TIMEOUT_MS = 8000;

	connectedCallback() {
		this.provider = this.dataset.provider || 'yotpo';
		this.adapter = REVIEWS_ADAPTERS[this.provider];
		this.#wireWriteReview();
		this.#wireReadAll();
		const appKey = this.dataset.appKey;
		const productId = this.dataset.productId;
		if (!this.adapter || !appKey || !productId) {
			this.#setError();
			return;
		}
		this.#fetchReviews(appKey, productId)
			.then((payload) => this.#render(payload))
			.catch(() => this.#setError());
	}

	// Reveal the hidden full reviews widget (collapse mode). The "Read all" CTA is redundant
	// once revealed, so it hides itself.
	#revealFullWidget() {
		document.body.classList.add('product-reviews-hero--show-reviews-widget');
		const link = this.querySelector('[data-product-reviews-hero-read-all]');
		if (link) {
			link.hidden = true;
		}
	}

	// "Read all" CTA reveals the hidden full reviews widget, scrolls to it, then removes itself.
	#wireReadAll() {
		const link = this.querySelector('[data-product-reviews-hero-read-all]');
		if (!link) {
			return;
		}
		link.addEventListener('click', (event) => {
			event.preventDefault();
			this.#revealFullWidget();
			// Defer one frame so the CSS reveal applies before scrollIntoView measures position.
			requestAnimationFrame(() => {
				const target = this.#findReviewsWidgetAfterHero();
				target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		});
	}

	// First provider widget AFTER this hero — skips the bottomline star badge above (near the title).
	#findReviewsWidgetAfterHero() {
		if (!this.adapter) {
			return null;
		}
		const candidates = document.querySelectorAll(this.adapter.widgetSelectors);
		for (const widget of candidates) {
			if (this.compareDocumentPosition(widget) & Node.DOCUMENT_POSITION_FOLLOWING) {
				return widget;
			}
		}
		return null;
	}

	#wireWriteReview() {
		const trigger = this.querySelector('[data-product-reviews-hero-write-review]');
		if (!trigger) {
			return;
		}
		trigger.addEventListener('click', () => this.#openReviewForm());
	}

	#openReviewForm() {
		if (!this.adapter) {
			return;
		}
		// Clicking the provider button opens its own form (a modal); don't reveal the collapsed list here.
		// Widget loads async — poll for the button (click fires even if hidden), else scroll to it.
		const start = Date.now();
		const POLL_MS = 100;
		const TIMEOUT_MS = 4000;
		const tick = () => {
			const btn = this.adapter.findWriteReviewButton();
			if (btn) {
				btn.click();
				return;
			}
			if (Date.now() - start < TIMEOUT_MS) {
				setTimeout(tick, POLL_MS);
				return;
			}
			// Fallback: the button never appeared — reveal the widget and scroll to it so the form
			// stays reachable. Defer a frame so the CSS reveal applies before scrollIntoView measures.
			this.#revealFullWidget();
			requestAnimationFrame(() => {
				const target = this.#findReviewsWidgetAfterHero() || document.querySelector(this.adapter.widgetSelectors);
				target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		};
		tick();
	}

	async #fetchReviews(appKey, productId) {
		const cacheKey = `${ProductReviewsHero.CACHE_PREFIX}${this.provider}:${appKey}:${productId}`;
		const cached = this.#readCache(cacheKey);
		if (cached) {
			return cached;
		}
		const payload = await this.adapter.fetch({
			appKey,
			productId,
			limit: ProductReviewsHero.REVIEW_LIMIT,
			signal: AbortSignal.timeout(ProductReviewsHero.FETCH_TIMEOUT_MS),
		});
		this.#writeCache(cacheKey, payload);
		return payload;
	}

	#render({ average, total, distribution, reviews }) {
		const formatter = new Intl.NumberFormat();

		const score = this.querySelector('[data-rating-score]');
		if (score) {
			score.textContent = average.toFixed(1);
		}

		const count = this.querySelector('[data-rating-count]');
		if (count) {
			count.textContent = formatter.format(total);
		}

		const stars = this.querySelector('[data-rating-stars]');
		if (stars) {
			stars.style.setProperty('--rating-pct', `${(average / 5) * 100}%`);
		}

		for (let star = 1; star <= 5; star++) {
			const row = this.querySelector(`[data-distribution-row="${star}"]`);
			if (!row) {
				continue;
			}
			const starCount = Number(distribution[String(star)]) || 0;
			const percent = total > 0 ? (starCount / total) * 100 : 0;
			const fill = row.querySelector('[data-distribution-fill]');
			if (fill) {
				fill.style.setProperty('--fill', `${percent}%`);
			}
			const countElement = row.querySelector('[data-distribution-count]');
			if (countElement) {
				countElement.textContent = formatter.format(starCount);
			}
		}

		this.#renderReviewCards(reviews);
		this.#updateWriteReviewLabel(total);
		this.#updateReadAll(total);

		this.dataset.state = 'loaded';
	}

	#updateReadAll(totalReviews) {
		const link = this.querySelector('[data-product-reviews-hero-read-all]');
		if (link) {
			// Hide when the hero already shows every review — nothing extra to reveal — or when
			// the widget was already revealed (e.g. via the write-review flow) while data loaded.
			const revealed = document.body.classList.contains('product-reviews-hero--show-reviews-widget');
			link.hidden = revealed || totalReviews <= ProductReviewsHero.REVIEW_LIMIT;
		}
	}

	#renderReviewCards(reviews) {
		const list = this.querySelector('[data-cards]');
		const cardEls = this.querySelectorAll('[data-card]');
		if (!list || !cardEls.length) {
			return;
		}
		if (!reviews.length) {
			list.hidden = true;
			return;
		}
		list.hidden = false;
		cardEls.forEach((card, index) => {
			const review = reviews[index];
			if (!review) {
				card.hidden = true;
				return;
			}
			const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
			const stars = card.querySelector('[data-card-stars]');
			if (stars) {
				stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
				stars.setAttribute('aria-label', `${rating} out of 5 stars`);
			}
			const attr = card.querySelector('[data-card-attribute]');
			if (attr) {
				// Only show the pill for a flavor/variant attribute; hide it (label + value) otherwise.
				const labelElement = attr.querySelector('[data-card-attribute-label]');
				const valueElement = attr.querySelector('[data-card-attribute-value]');
				if (review.attributeLabel && review.attributeValue && labelElement && valueElement) {
					labelElement.textContent = review.attributeLabel;
					valueElement.textContent = review.attributeValue;
					attr.hidden = false;
				} else {
					attr.hidden = true;
				}
			}
			const title = card.querySelector('[data-card-title]');
			if (title) {
				title.textContent = review.title || '';
				title.hidden = !review.title;
			}
			const body = card.querySelector('[data-card-body]');
			if (body) {
				body.textContent = review.body || '';
				body.hidden = !review.body;
			}
			const author = card.querySelector('[data-card-author]');
			if (author) {
				author.textContent = review.author || 'Anonymous';
			}
			const location = card.querySelector('[data-card-location]');
			if (location) {
				if (review.location) {
					location.textContent = review.location;
					location.hidden = false;
				} else {
					location.hidden = true;
				}
			}
			const verified = card.querySelector('[data-card-verified]');
			if (verified) {
				verified.hidden = !review.verified;
			}
			card.hidden = false;
		});
	}

	#updateWriteReviewLabel(totalReviews) {
		if (totalReviews > 0) {
			return;
		}
		const label = this.querySelector('[data-write-review-label]');
		if (label) {
			label.textContent = ProductReviewsHero.EMPTY_BUTTON_LABEL;
		}
	}

	#setError() {
		this.dataset.state = 'error';
		// Blank the API-driven content; featured quote + write-review button stay as the floor.
		this.querySelector('[data-rating-score]')?.replaceChildren();
		this.querySelector('[data-rating-count]')?.replaceChildren();
		const list = this.querySelector('[data-cards]');
		if (list) {
			list.hidden = true;
		}
	}

	#readCache(key) {
		try {
			const raw = sessionStorage.getItem(key);
			if (!raw) {
				return null;
			}
			const { timestamp, data } = JSON.parse(raw);
			if (Date.now() - timestamp > ProductReviewsHero.CACHE_TTL_MS) {
				return null;
			}
			return data;
		} catch {
			return null;
		}
	}

	#writeCache(key, data) {
		try {
			sessionStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
		} catch {
			// sessionStorage may be full or blocked; cache is best-effort.
		}
	}
}

if (!customElements.get('product-reviews-hero')) {
	customElements.define('product-reviews-hero', ProductReviewsHero);
}
